import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CalendarOff, Plus, CheckCircle, XCircle, Download, Shield, Trash2, Pencil, Save, ChevronDown, ChevronUp, AlertTriangle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LEAVE_TYPES = ['Casual Leave', 'Earned Leave', 'Privilege Leave', 'Sick Leave', 'Comp Off', 'Work From Home', 'Leave Without Pay'];
const COMPANY_ROLES = ['admin', 'team_leader', 'recruiter', 'intern', 'it', 'finance', 'marketing'];

/* ==================== REUSABLE FORM COMPONENTS (outside to prevent cursor jumping) ==================== */

function PolicyFieldInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1 h-8 rounded border-slate-200 text-sm" />
    </div>
  );
}

function PolicyFieldCheck({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked || false} onChange={e => onChange(e.target.checked)} className="rounded border-slate-300" />
      <span className="text-xs font-semibold text-slate-600">{label}</span>
    </label>
  );
}

function PolicyFieldTextarea({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1 min-h-[60px] rounded border-slate-200 text-sm" />
    </div>
  );
}

function PolicySectionHeader({ label, isOpen, onToggle }) {
  return (
    <button onClick={onToggle} className="w-full flex items-center justify-between py-2 text-left">
      <span className="text-sm font-bold text-blue-900 uppercase tracking-wider">{label}</span>
      {isOpen ? <ChevronUp size={16} className="text-blue-500" /> : <ChevronDown size={16} className="text-blue-500" />}
    </button>
  );
}

/* ==================== MAIN COMPONENT ==================== */

export default function LeaveManagement() {
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [payrollData, setPayrollData] = useState([]);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingWithFilter, setPendingWithFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [tab, setTab] = useState('leaves');
  const [payrollMonth, setPayrollMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formData, setFormData] = useState({ leave_type: 'Casual Leave', start_date: '', end_date: '', reason: '', is_half_day: false });
  const [leaveTypes, setLeaveTypes] = useState(DEFAULT_LEAVE_TYPES);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  // Stable accessor for sub-components (LeavePolicyTab / AdminControlsTab).
  // They invoke this once per call to read the latest headers without depending
  // on the parent's `headers` identity.
  const getHeaders = useCallback(() => headers, [headers]);
  const canApprove = user && (user.role === 'admin' || user.role === 'team_leader');
  const isAdmin = user && user.role === 'admin';

  const fetchLeaves = useCallback(async () => {
    try {
      let url = `${API}/hrms/leaves`;
      const params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (pendingWithFilter) params.push(`pending_with=${pendingWithFilter}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url, { headers });
      setLeaves(res.data);
    } catch (e) { toast.error('Failed to fetch leaves'); }
  }, [headers, statusFilter, pendingWithFilter]);

  const fetchBalance = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/hrms/leave-balance/${user.id}`, { headers });
      setLeaveBalance(res.data);
    } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
  }, [user, headers]);

  const fetchPayroll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hrms/payroll-summary?month=${payrollMonth}`, { headers });
      setPayrollData(res.data);
    } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
  }, [payrollMonth, headers]);

  const fetchPendingSummary = useCallback(async () => {
    if (!canApprove) return;
    try {
      const res = await axios.get(`${API}/hrms/leaves/pending-summary`, { headers });
      setPendingSummary(res.data);
    } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
  }, [canApprove, headers]);

  useEffect(() => { fetchLeaves(); fetchBalance(); fetchPendingSummary(); }, [fetchLeaves, fetchBalance, fetchPendingSummary]);
  useEffect(() => { if (canApprove && tab === 'payroll') fetchPayroll(); }, [canApprove, tab, fetchPayroll]);

  // Fetch leave policy to dynamically populate leave types
  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const res = await axios.get(`${API}/hrms/leave-policy`, { headers });
        if (res.data?.leave_types?.length > 0) {
          const policyTypes = res.data.leave_types.map(lt => lt.name);
          // Merge policy types with LWP (always available)
          const merged = [...new Set([...policyTypes, 'Leave Without Pay'])];
          setLeaveTypes(merged);
        }
      } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
    };
    if (token) fetchPolicy();
  }, [token, headers]);

  const [leaveOverride, setLeaveOverride] = useState(null);

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hrms/leaves`, formData, { headers });
      toast.success('Leave applied'); setOpen(false);
      setFormData({ leave_type: 'Casual Leave', start_date: '', end_date: '', reason: '' });
      fetchLeaves(); fetchBalance(); fetchPendingSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply leave'); }
  };

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectLeaveId, setRejectLeaveId] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  const handleAction = async (leaveId, action, comment = '') => {
    try {
      const payload = { action };
      if (action === 'rejected' && comment) payload.rejection_comment = comment;
      const res = await axios.put(`${API}/hrms/leaves/${leaveId}/approve`, payload, { headers });
      toast.success(res.data.message || `Leave ${action}`); fetchLeaves(); fetchBalance(); fetchPendingSummary();
      if (action === 'rejected') { setRejectOpen(false); setRejectComment(''); }
    } catch (e) { toast.error('Failed to update leave'); }
  };

  const openRejectDialog = (leaveId) => {
    setRejectLeaveId(leaveId);
    setRejectComment('');
    setRejectOpen(true);
  };

  const handleDeleteLeave = async (leaveId, userName) => {
    if (!window.confirm(`Delete leave record for ${userName || 'this employee'}? This cannot be undone. If the leave was approved, the balance will be restored.`)) return;
    try {
      await axios.delete(`${API}/hrms/leaves/${leaveId}`, { headers });
      toast.success('Leave deleted');
      fetchLeaves(); fetchBalance(); fetchPendingSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete leave'); }
  };

  const downloadLeaves = async () => {
    try {
      const url = statusFilter ? `${API}/hrms/leaves/export?status=${statusFilter}` : `${API}/hrms/leaves/export`;
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = 'leave_records.csv'; a.click();
      toast.success('Leave records downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  const downloadPayroll = async () => {
    try {
      const res = await axios.get(`${API}/hrms/payroll-summary/export?month=${payrollMonth}`, { headers, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = `payroll_summary_${payrollMonth}.csv`; a.click();
      toast.success('Payroll summary downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  const submitLeaveOverride = async () => {
    if (!leaveOverride) return;
    try {
      const res = await axios.put(`${API}/hrms/leaves/${leaveOverride.id}/admin-override`, {
        status: leaveOverride.status, reason: leaveOverride.reason
      }, { headers });
      toast.success(res.data.message); setLeaveOverride(null); fetchLeaves(); fetchBalance(); fetchPendingSummary();
    } catch (e) { toast.error('Override failed'); }
  };

  const pendingCount = leaves.filter(l => l.status === 'pending').length;
  const [workingDaysInfo, setWorkingDaysInfo] = useState(null);
  const leaveDays = workingDaysInfo?.working_days || 0;

  useEffect(() => {
    if (formData.start_date && formData.end_date && formData.end_date >= formData.start_date) {
      axios.get(`${API}/hrms/leaves/working-days?start_date=${formData.start_date}&end_date=${formData.end_date}`, { headers })
        .then(res => setWorkingDaysInfo(res.data))
        .catch(() => setWorkingDaysInfo(null));
    } else {
      setWorkingDaysInfo(null);
    }
  }, [formData.start_date, formData.end_date]);

  const payrollTotals = useMemo(() => {
    return payrollData.reduce((acc, r) => ({
      present: acc.present + r.present_days,
      hours: acc.hours + r.total_hours,
      leaves: acc.leaves + r.total_leave_days,
      lwp: acc.lwp + r.lwp_days,
    }), { present: 0, hours: 0, leaves: 0, lwp: 0 });
  }, [payrollData]);

  return (
    <div>
      {/* Rejection Comment Modal - must be at top level */}
      {rejectOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="rejection-modal-overlay">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRejectOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10">
            <h3 className="text-lg font-bold text-red-900 mb-1">Reject Leave</h3>
            <p className="text-sm text-slate-500 mb-4">Provide a reason for rejecting this leave request. The applicant will see this comment.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-600">Rejection Reason</Label>
                <textarea data-testid="rejection-comment-input" value={rejectComment} onChange={e => setRejectComment(e.target.value)} rows={3}
                  className="mt-1 w-full rounded border border-red-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  placeholder="e.g. Insufficient leave balance, project deadline, etc." autoFocus />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRejectOpen(false)} className="text-sm">Cancel</Button>
                <Button data-testid="confirm-reject-btn" onClick={() => handleAction(rejectLeaveId, 'rejected', rejectComment)}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold text-sm">
                  <XCircle size={14} className="mr-1" />Reject Leave
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="leaves-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Leave Management</h1>
          <p className="text-slate-600">Apply and manage leaves{canApprove && pendingCount > 0 ? ` — ${pendingCount} pending approval` : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-slate-100 rounded p-1">
            <button data-testid="tab-leaves" onClick={() => setTab('leaves')} className={`px-4 py-1.5 rounded text-xs font-bold ${tab === 'leaves' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>Leaves</button>
            {canApprove && <button data-testid="tab-payroll" onClick={() => setTab('payroll')} className={`px-4 py-1.5 rounded text-xs font-bold ${tab === 'payroll' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>Payroll Summary</button>}
            <button data-testid="tab-policy" onClick={() => setTab('policy')} className={`px-4 py-1.5 rounded text-xs font-bold ${tab === 'policy' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>Leave Policy</button>
            {isAdmin && <button data-testid="tab-admin" onClick={() => setTab('admin')} className={`px-4 py-1.5 rounded text-xs font-bold ${tab === 'admin' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>Admin Controls</button>}
          </div>
          {tab === 'leaves' && <Button data-testid="apply-leave-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Apply Leave</Button>}
        </div>
      </div>

      {/* Pending TL Approval Banner (Admin only) */}
      {isAdmin && pendingSummary && pendingSummary.pending_tl > 0 && tab === 'leaves' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-3" data-testid="pending-tl-banner">
          <AlertTriangle size={18} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <span className="text-sm font-bold text-amber-800">{pendingSummary.pending_tl} leave(s) pending Team Leader approval</span>
            <span className="text-xs text-amber-600 ml-2">These are awaiting TL action. You can also approve/reject directly.</span>
          </div>
          <Button data-testid="filter-pending-tl" onClick={() => { setStatusFilter('pending'); setPendingWithFilter('team_leader'); }} variant="outline" className="text-xs h-7 rounded border-amber-300 text-amber-700">
            <Users size={12} className="mr-1" />Show TL Pending
          </Button>
        </div>
      )}

      {/* Leave Balance Cards */}
      {leaveBalance?.balances && tab === 'leaves' && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6" data-testid="leave-balances">
          {Object.entries(leaveBalance.balances).map(([type, bal]) => (
            <div key={type} className="bg-white border border-blue-100 rounded shadow-sm p-3 text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{type.replace('Leave ', '').replace('Without ', 'W/O ')}</p>
              <p className={`text-2xl font-extrabold ${bal > 0 ? 'text-blue-900' : type === 'Leave Without Pay' ? 'text-slate-400' : 'text-red-600'}`}>{type === 'Leave Without Pay' ? 'LWP' : bal}</p>
              <p className="text-xs text-slate-400 mt-0.5">days</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'leaves' && (
        <>
          <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex items-end gap-4 flex-wrap">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
              <select data-testid="filter-leave-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPendingWithFilter(''); }} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
                <option value="">All</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
              </select>
            </div>
            {isAdmin && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Pending With</Label>
                <select data-testid="filter-pending-with" value={pendingWithFilter} onChange={(e) => setPendingWithFilter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
                  <option value="">All</option><option value="team_leader">Team Leader</option><option value="admin">Admin</option>
                </select>
              </div>
            )}
            <span className="text-xs text-slate-500 ml-auto">{leaves.length} records</span>
            <div className="relative min-w-[220px]">
              <Input data-testid="leaves-search-input" placeholder="Search by employee, type, reason…" value={searchText} onChange={e => setSearchText(e.target.value)} className="h-9 rounded border-blue-200 text-sm" />
            </div>
            <Button data-testid="download-leaves-btn" onClick={downloadLeaves} variant="outline" className="border-blue-300 text-blue-700 rounded text-xs h-9">
              <Download size={14} className="mr-1.5" />Download CSV
            </Button>
          </div>

          <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-50 border-b border-blue-100">
                  <TableHead className="font-bold text-blue-900">Employee</TableHead>
                  <TableHead className="font-bold text-blue-900">Type</TableHead>
                  <TableHead className="font-bold text-blue-900">From</TableHead>
                  <TableHead className="font-bold text-blue-900">To</TableHead>
                  <TableHead className="font-bold text-blue-900 text-center">Days</TableHead>
                  <TableHead className="font-bold text-blue-900">Reason</TableHead>
                  <TableHead className="font-bold text-blue-900">Status</TableHead>
                  <TableHead className="font-bold text-blue-900">Pending With</TableHead>
                  {canApprove && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const q = searchText.trim().toLowerCase();
                  const filteredLeaves = q ? leaves.filter(l =>
                    (l.user_name || '').toLowerCase().includes(q) ||
                    (l.leave_type || '').toLowerCase().includes(q) ||
                    (l.reason || '').toLowerCase().includes(q) ||
                    (l.status || '').toLowerCase().includes(q)
                  ) : leaves;
                  return filteredLeaves.length === 0 ? (
                  <TableRow><TableCell colSpan={canApprove ? 9 : 8} className="text-center text-slate-500 py-8"><CalendarOff size={48} className="mx-auto mb-2 text-slate-300" />{q ? 'No leave records match your search.' : 'No leave records'}</TableCell></TableRow>
                ) : filteredLeaves.map(leave => (
                  <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`} className="hover:bg-blue-50">
                    <TableCell className="font-semibold text-blue-900">{leave.user_name}</TableCell>
                    <TableCell><span className={`text-xs font-bold px-2 py-0.5 rounded ${leave.leave_type === 'Leave Without Pay' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>{leave.leave_type}</span></TableCell>
                    <TableCell className="text-sm">{leave.start_date}</TableCell>
                    <TableCell className="text-sm">{leave.end_date}</TableCell>
                    <TableCell className="text-center font-bold">{leave.days || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[250px]"><span className="whitespace-pre-wrap break-words">{leave.reason || '-'}</span></TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${leave.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : leave.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{leave.status}</span>
                      {leave.status === 'approved' && leave.balance_deducted > 0 && (
                        <span className="block text-xs text-emerald-600 mt-0.5">-{leave.balance_deducted}d deducted</span>
                      )}
                      {leave.status === 'rejected' && leave.rejection_comment && (
                        <span className="block text-xs text-red-600 mt-1 italic max-w-[200px] whitespace-pre-wrap break-words" data-testid={`rejection-comment-${leave.id}`}>{leave.rejection_comment}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {leave.status === 'pending' ? (
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 text-xs font-bold rounded inline-block w-fit ${leave.pending_with === 'team_leader' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {leave.pending_with === 'team_leader' ? 'Team Leader' : 'Admin'}
                          </span>
                          {leave.escalated && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-orange-100 text-orange-700 inline-block w-fit" data-testid={`escalated-${leave.id}`}>
                              Escalated
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">{leave.approved_by_name || '-'}</span>
                      )}
                    </TableCell>
                    {canApprove && (
                      <TableCell>
                        {leave.status === 'pending' ? (
                          <div className="flex gap-1">
                            <Button size="sm" data-testid={`approve-leave-${leave.id}`} onClick={() => handleAction(leave.id, 'approved')} className="bg-emerald-600 text-white text-xs h-7 rounded"><CheckCircle size={12} className="mr-1" />Approve</Button>
                            <Button size="sm" data-testid={`reject-leave-${leave.id}`} onClick={() => openRejectDialog(leave.id)} variant="outline" className="text-red-600 border-red-300 text-xs h-7 rounded"><XCircle size={12} className="mr-1" />Reject</Button>
                          </div>
                        ) : isAdmin && (
                          <Button size="sm" data-testid={`override-leave-${leave.id}`} onClick={() => setLeaveOverride({ id: leave.id, status: leave.status, reason: '', user_name: leave.user_name })} variant="outline" className="text-xs h-6 rounded border-amber-300 text-amber-700"><Pencil size={10} className="mr-1" />Override</Button>
                        )}
                        {leave.admin_override && <span className="block text-xs text-amber-600 mt-0.5" title={leave.override_reason}>Admin overridden</span>}
                        {isAdmin && (
                          <Button size="sm" data-testid={`delete-leave-${leave.id}`} onClick={() => handleDeleteLeave(leave.id, leave.user_name)} variant="ghost" className="text-red-600 hover:text-red-800 hover:bg-red-50 h-7 px-2 ml-1" title="Delete leave">
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ));
                })()}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Payroll Summary Tab */}
      {tab === 'payroll' && canApprove && (
        <>
          <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex items-end gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Month</Label>
              <Input data-testid="payroll-month" type="month" value={payrollMonth} onChange={(e) => setPayrollMonth(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-48" />
            </div>
            <span className="text-xs text-slate-500 ml-auto">{payrollData.length} employees</span>
            <Button data-testid="download-payroll-btn" onClick={downloadPayroll} variant="outline" className="border-blue-300 text-blue-700 rounded text-xs h-9">
              <Download size={14} className="mr-1.5" />Download CSV
            </Button>
          </div>
          <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-50 border-b border-blue-100">
                  <TableHead className="font-bold text-blue-900">Employee</TableHead>
                  <TableHead className="font-bold text-blue-900">Role</TableHead>
                  <TableHead className="font-bold text-blue-900 text-center">Present Days</TableHead>
                  <TableHead className="font-bold text-blue-900 text-center">Hours</TableHead>
                  <TableHead className="font-bold text-blue-900 text-center">Leave Days</TableHead>
                  <TableHead className="font-bold text-blue-900 text-center">LWP Days</TableHead>
                  <TableHead className="font-bold text-blue-900">Leave Breakdown</TableHead>
                  <TableHead className="font-bold text-blue-900">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollData.map(row => (
                  <TableRow key={row.user_id} data-testid={`payroll-row-${row.user_id}`} className="hover:bg-blue-50">
                    <TableCell className="font-semibold text-blue-900">{row.user_name}</TableCell>
                    <TableCell><span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded uppercase">{row.role?.replace('_', ' ')}</span></TableCell>
                    <TableCell className="text-center font-bold text-emerald-700">{row.present_days}</TableCell>
                    <TableCell className="text-center font-bold">{row.total_hours}</TableCell>
                    <TableCell className="text-center font-bold text-amber-700">{row.total_leave_days}</TableCell>
                    <TableCell className="text-center font-bold text-red-700">{row.lwp_days}</TableCell>
                    <TableCell className="text-xs">
                      {Object.entries(row.leave_breakdown || {}).map(([type, days]) => (
                        <span key={type} className="block">{type}: {days}d</span>
                      ))}
                      {Object.keys(row.leave_breakdown || {}).length === 0 && <span className="text-slate-400">None</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {Object.entries(row.leave_balances || {}).filter(([, v]) => typeof v === 'number' && v > 0).map(([type, bal]) => (
                        <span key={type} className="block">{type.replace('Leave ', '').replace('Without ', 'W/O ')}: {bal}</span>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-blue-100 border-t-2 border-blue-300" data-testid="payroll-totals">
                  <TableCell className="font-extrabold text-blue-900" colSpan={2}>TOTAL</TableCell>
                  <TableCell className="text-center font-extrabold text-emerald-800">{payrollTotals.present}</TableCell>
                  <TableCell className="text-center font-extrabold">{payrollTotals.hours}</TableCell>
                  <TableCell className="text-center font-extrabold text-amber-800">{payrollTotals.leaves}</TableCell>
                  <TableCell className="text-center font-extrabold text-red-800">{payrollTotals.lwp}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Leave Policy Tab */}
      {tab === 'policy' && <LeavePolicyTab getHeaders={getHeaders} isAdmin={isAdmin} />}

      {/* Admin Controls Tab */}
      {tab === 'admin' && isAdmin && <AdminControlsTab getHeaders={getHeaders} onRefresh={() => { fetchLeaves(); fetchBalance(); fetchPendingSummary(); }} />}

      {/* Leave Override Dialog (Admin) */}
      <Dialog open={!!leaveOverride} onOpenChange={() => setLeaveOverride(null)}>
        <DialogContent className="rounded max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">Override Leave — {leaveOverride?.user_name}</DialogTitle>
            <DialogDescription>Change the status of this leave with an audit trail</DialogDescription>
          </DialogHeader>
          {leaveOverride && (
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs font-bold text-blue-900">New Status</Label>
                <select data-testid="leave-override-status" value={leaveOverride.status} onChange={e => setLeaveOverride(p => ({ ...p, status: e.target.value }))} className="mt-1 flex h-9 w-full rounded border border-blue-200 bg-white px-3 text-sm">
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold text-blue-900">Reason (mandatory)</Label>
                <Input data-testid="leave-override-reason" value={leaveOverride.reason} onChange={e => setLeaveOverride(p => ({ ...p, reason: e.target.value }))} placeholder="Reason for override..." className="mt-1 h-9 rounded border-blue-200 text-sm" />
              </div>
              <Button data-testid="submit-leave-override-btn" onClick={submitLeaveOverride} disabled={!leaveOverride.reason} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
                <Save size={14} className="mr-2" />Apply Override
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Leave Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Apply Leave</DialogTitle>
            <DialogDescription>Submit a leave request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Leave Type</Label>
              <select data-testid="leave-type-select" value={formData.leave_type} onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                {leaveTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {formData.leave_type === 'Leave Without Pay' && <p className="text-xs text-red-600 mt-1 font-medium">LWP will be deducted from salary</p>}
              {leaveBalance?.balances && formData.leave_type !== 'Leave Without Pay' && (
                <p className="text-xs text-blue-600 mt-1">Balance: {leaveBalance.balances[formData.leave_type] ?? 'N/A'} days</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From</Label>
                <Input data-testid="leave-start" type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To</Label>
                <Input data-testid="leave-end" type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
            </div>
            {workingDaysInfo && (
              <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200 space-y-1">
                <p className="font-bold text-blue-900" data-testid="leave-working-days">{formData.is_half_day ? '0.5' : workingDaysInfo.working_days} working day(s) will be deducted</p>
                {(workingDaysInfo.excluded_weekends > 0 || workingDaysInfo.excluded_holidays > 0) && !formData.is_half_day && (
                  <div className="text-xs text-slate-600 space-y-0.5">
                    {workingDaysInfo.excluded_weekends > 0 && <p>{workingDaysInfo.excluded_weekends} weekend day(s) excluded</p>}
                    {workingDaysInfo.excluded_holidays > 0 && <p>{workingDaysInfo.excluded_holidays} holiday(s) excluded</p>}
                    {workingDaysInfo.excluded_details?.length > 0 && (
                      <div className="mt-1 pt-1 border-t border-blue-200">
                        {workingDaysInfo.excluded_details.map((d, i) => (
                          <p key={`leave-${i}`} className="text-xs text-slate-500">{d.date} — {d.reason}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-3">
              <input data-testid="half-day-toggle" type="checkbox" id="half-day" checked={formData.is_half_day} onChange={e => setFormData({ ...formData, is_half_day: e.target.checked, end_date: e.target.checked ? formData.start_date : formData.end_date })} className="rounded border-blue-300 h-4 w-4" />
              <Label htmlFor="half-day" className="text-sm font-semibold text-blue-900 cursor-pointer">Apply as half-day leave (0.5 day)</Label>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Reason</Label>
              <textarea data-testid="leave-reason" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Reason for leave..." /></div>
            <Button type="submit" data-testid="submit-leave-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Apply Leave</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== LEAVE POLICY TAB ==================== */

const EMPTY_POLICY = {
  name: '',
  eligibility: { categories: '', probation_period: '', min_tenure_months: '' },
  leave_types: [{ name: '', days_per_year: '', carry_forward: false, encashable: false, max_carry_forward_days: '', min_tenure_months: '' }],
  accrual_rules: { method: 'annual', credit_date: '1st of year', proration_for_new_joiners: true, year_start: 'calendar' },
  application_process: { min_notice_days: '', max_consecutive_days: '', half_day_allowed: false, notes: '' },
  approval_workflow: { first_approver: 'team_leader', second_approver: 'admin', escalation_days: '', auto_approve_wfh: false, notes: '' },
  documentation: { sick_leave_certificate_days: '', other_docs: '' },
  restrictions: { blackout_dates: '', max_consecutive_days: '', project_based: false, notes: '' },
  compliance: { statutory_leaves: '', maternity_weeks: '', paternity_days: '', notes: '' },
  tracking: { integrated_with_payroll: true, dashboard_visibility: true, notes: '' },
};

function LeavePolicyTab({ getHeaders, isAdmin }) {
  const [policies, setPolicies] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(JSON.parse(JSON.stringify(EMPTY_POLICY)));
  const [showForm, setShowForm] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const fetchPolicies = useCallback(async () => {
    try { const res = await axios.get(`${API}/hrms/leave-policy`, { headers: getHeaders() }); setPolicies(res.data); } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
  }, [getHeaders]);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const openNew = () => {
    setForm(JSON.parse(JSON.stringify(EMPTY_POLICY)));
    setEditingId(null); setShowForm(true);
  };

  const openEdit = (policy) => {
    setForm({
      name: policy.name || '',
      eligibility: { ...EMPTY_POLICY.eligibility, ...(policy.eligibility || {}) },
      leave_types: policy.leave_types?.length > 0 ? policy.leave_types : [...EMPTY_POLICY.leave_types],
      accrual_rules: { ...EMPTY_POLICY.accrual_rules, ...(policy.accrual_rules || {}) },
      application_process: { ...EMPTY_POLICY.application_process, ...(policy.application_process || {}) },
      approval_workflow: { ...EMPTY_POLICY.approval_workflow, ...(policy.approval_workflow || {}) },
      documentation: { ...EMPTY_POLICY.documentation, ...(policy.documentation || {}) },
      restrictions: { ...EMPTY_POLICY.restrictions, ...(policy.restrictions || {}) },
      compliance: { ...EMPTY_POLICY.compliance, ...(policy.compliance || {}) },
      tracking: { ...EMPTY_POLICY.tracking, ...(policy.tracking || {}) },
    });
    setEditingId(policy.id); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Policy name is required'); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/hrms/leave-policy/${editingId}`, form, { headers: getHeaders() });
        toast.success('Policy updated');
      } else {
        await axios.post(`${API}/hrms/leave-policy`, form, { headers: getHeaders() });
        toast.success('Policy created');
      }
      setShowForm(false); fetchPolicies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save policy'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this leave policy?')) return;
    try {
      await axios.delete(`${API}/hrms/leave-policy/${id}`, { headers: getHeaders() });
      toast.success('Policy deleted'); fetchPolicies();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const addLeaveType = () => setForm(prev => ({ ...prev, leave_types: [...prev.leave_types, { name: '', days_per_year: '', carry_forward: false, encashable: false, max_carry_forward_days: '', min_tenure_months: '' }] }));
  const removeLeaveType = (i) => setForm(prev => ({ ...prev, leave_types: prev.leave_types.filter((_, idx) => idx !== i) }));
  const updateLeaveType = (i, field, val) => setForm(prev => {
    const updated = [...prev.leave_types];
    updated[i] = { ...updated[i], [field]: val };
    return { ...prev, leave_types: updated };
  });

  const updateField = (section, field, val) => setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: val } }));

  return (
    <div data-testid="leave-policy-section">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-bold text-blue-900">Leave Policies ({policies.length})</p>
        {isAdmin && <Button data-testid="add-policy-btn" onClick={openNew} className="bg-blue-700 hover:bg-blue-800 text-white rounded text-xs"><Plus size={14} className="mr-1" />Create Leave Policy</Button>}
      </div>

      {policies.length === 0 && !showForm && (
        <div className="bg-white border border-blue-100 rounded-lg p-12 text-center">
          <Shield size={48} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">No leave policies defined</p>
          {isAdmin && <p className="text-xs text-slate-400 mt-1">Create a policy to standardize leave rules across the organization</p>}
        </div>
      )}

      {policies.map(policy => (
        <div key={policy.id} className="bg-white border border-blue-100 rounded-lg shadow-sm mb-4 overflow-hidden" data-testid={`policy-card-${policy.id}`}>
          <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-blue-600" />
              <span className="font-bold text-blue-900">{policy.name}</span>
              {policy.is_active && <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded">Active</span>}
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-xs text-slate-500">By {policy.created_by_name} | {policy.created_at?.slice(0, 10)}</span>
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" data-testid={`edit-policy-${policy.id}`} onClick={() => openEdit(policy)} className="text-xs h-7 rounded border-blue-300"><Pencil size={12} className="mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" data-testid={`delete-policy-${policy.id}`} onClick={() => handleDelete(policy.id)} className="text-xs h-7 rounded border-red-300 text-red-600"><Trash2 size={12} className="mr-1" />Delete</Button>
                </>
              )}
            </div>
          </div>
          <PolicyReadView policy={policy} />
        </div>
      ))}

      {/* Policy Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900">{editingId ? 'Edit' : 'Create'} Leave Policy</DialogTitle>
            <DialogDescription>Configure all aspects of the leave policy</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <PolicyFieldInput label="Policy Name" value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="e.g., Standard Leave Policy 2026" />

            {/* Eligibility */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Eligibility" isOpen={expandedSections.eligibility !== false} onToggle={() => toggleSection('eligibility')} />
              {expandedSections.eligibility !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Eligible Categories</Label>
                    <select value={form.eligibility.categories || ''} onChange={e => updateField('eligibility', 'categories', e.target.value)} className="mt-1 flex h-8 w-full rounded border border-slate-200 bg-white px-3 text-sm">
                      <option value="">Select...</option>
                      <option value="All full-time employees">All full-time employees</option>
                      <option value="Confirmed employees only">Confirmed employees only</option>
                      {COMPANY_ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ').toUpperCase()}</option>)}
                    </select>
                  </div>
                  <PolicyFieldInput label="Probation Period" value={form.eligibility.probation_period} onChange={v => updateField('eligibility', 'probation_period', v)} placeholder="e.g., 6 months" />
                  <PolicyFieldInput label="Min Tenure (Months)" value={form.eligibility.min_tenure_months} onChange={v => updateField('eligibility', 'min_tenure_months', v)} placeholder="e.g., 3" type="number" />
                </div>
              )}
            </div>

            {/* Leave Types */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Types of Leave" isOpen={expandedSections.leave_types !== false} onToggle={() => toggleSection('leave_types')} />
              {expandedSections.leave_types !== false && (
                <div className="space-y-3 mt-2">
                  {form.leave_types.map((lt, i) => (
                    <div key={`lt-${i}`} className="grid grid-cols-6 gap-2 items-end bg-slate-50 p-2 rounded">
                      <PolicyFieldInput label="Leave Type" value={lt.name} onChange={v => updateLeaveType(i, 'name', v)} placeholder="e.g., Casual Leave" />
                      <PolicyFieldInput label="Days/Year" value={lt.days_per_year} onChange={v => updateLeaveType(i, 'days_per_year', v)} placeholder="12" type="number" />
                      <PolicyFieldInput label="Min Tenure (months)" value={lt.min_tenure_months} onChange={v => updateLeaveType(i, 'min_tenure_months', v)} placeholder="0" type="number" />
                      <PolicyFieldCheck label="Carry Forward" checked={lt.carry_forward} onChange={v => updateLeaveType(i, 'carry_forward', v)} />
                      <PolicyFieldCheck label="Encashable" checked={lt.encashable} onChange={v => updateLeaveType(i, 'encashable', v)} />
                      <PolicyFieldInput label="Max CF Days" value={lt.max_carry_forward_days} onChange={v => updateLeaveType(i, 'max_carry_forward_days', v)} placeholder="5" type="number" />
                      <Button size="sm" variant="outline" onClick={() => removeLeaveType(i)} className="text-red-500 border-red-200 h-8 text-xs"><Trash2 size={12} /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={addLeaveType} className="text-xs border-blue-300 text-blue-700"><Plus size={12} className="mr-1" />Add Leave Type</Button>
                </div>
              )}
            </div>

            {/* Accrual Rules */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Accrual Rules" isOpen={expandedSections.accrual !== false} onToggle={() => toggleSection('accrual')} />
              {expandedSections.accrual !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Accrual Method</Label>
                    <select value={form.accrual_rules.method || 'annual'} onChange={e => updateField('accrual_rules', 'method', e.target.value)} className="mt-1 flex h-8 w-full rounded border border-slate-200 bg-white px-3 text-sm">
                      <option value="annual">Annual (credited at start of year)</option>
                      <option value="monthly">Monthly (credited each month)</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Year Start (Reset Date)</Label>
                    <select data-testid="year-start-select" value={form.accrual_rules.year_start || 'calendar'} onChange={e => updateField('accrual_rules', 'year_start', e.target.value)} className="mt-1 flex h-8 w-full rounded border border-slate-200 bg-white px-3 text-sm">
                      <option value="calendar">Calendar Year (Jan - Dec)</option>
                      <option value="financial">Financial Year (Apr - Mar)</option>
                    </select>
                  </div>
                  <PolicyFieldInput label="Credit Date" value={form.accrual_rules.credit_date} onChange={v => updateField('accrual_rules', 'credit_date', v)} placeholder="e.g., 1st of each month" />
                  <PolicyFieldCheck label="Prorate for new joiners (based on joining date)" checked={form.accrual_rules.proration_for_new_joiners} onChange={v => updateField('accrual_rules', 'proration_for_new_joiners', v)} />
                </div>
              )}
            </div>

            {/* Application Process */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Application Process" isOpen={expandedSections.application !== false} onToggle={() => toggleSection('application')} />
              {expandedSections.application !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <PolicyFieldInput label="Min Notice Days" value={form.application_process.min_notice_days} onChange={v => updateField('application_process', 'min_notice_days', v)} placeholder="e.g., 3" type="number" />
                  <PolicyFieldInput label="Max Consecutive Days" value={form.application_process.max_consecutive_days} onChange={v => updateField('application_process', 'max_consecutive_days', v)} placeholder="e.g., 10" type="number" />
                  <PolicyFieldCheck label="Half-day leave allowed" checked={form.application_process.half_day_allowed} onChange={v => updateField('application_process', 'half_day_allowed', v)} />
                  <PolicyFieldTextarea label="Additional Notes" value={form.application_process.notes} onChange={v => updateField('application_process', 'notes', v)} placeholder="Any additional application process notes..." />
                </div>
              )}
            </div>

            {/* Approval Workflow */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Approval Workflow" isOpen={expandedSections.approval !== false} onToggle={() => toggleSection('approval')} />
              {expandedSections.approval !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">First Approver</Label>
                    <select data-testid="first-approver-select" value={form.approval_workflow.first_approver || 'team_leader'} onChange={e => updateField('approval_workflow', 'first_approver', e.target.value)} className="mt-1 flex h-8 w-full rounded border border-slate-200 bg-white px-3 text-sm">
                      {COMPANY_ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ').toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Second Approver (Escalation)</Label>
                    <select data-testid="second-approver-select" value={form.approval_workflow.second_approver || 'admin'} onChange={e => updateField('approval_workflow', 'second_approver', e.target.value)} className="mt-1 flex h-8 w-full rounded border border-slate-200 bg-white px-3 text-sm">
                      {COMPANY_ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ').toUpperCase()}</option>)}
                    </select>
                  </div>
                  <PolicyFieldInput label="Escalation After (days)" value={form.approval_workflow.escalation_days} onChange={v => updateField('approval_workflow', 'escalation_days', v)} placeholder="e.g., 3" type="number" />
                  <PolicyFieldCheck label="Auto-approve WFH requests" checked={form.approval_workflow.auto_approve_wfh} onChange={v => updateField('approval_workflow', 'auto_approve_wfh', v)} />
                  <PolicyFieldTextarea label="Workflow Notes" value={form.approval_workflow.notes} onChange={v => updateField('approval_workflow', 'notes', v)} placeholder="e.g., Leaves > 3 days need admin approval..." />
                </div>
              )}
            </div>

            {/* Documentation */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Documentation Requirements" isOpen={expandedSections.documentation !== false} onToggle={() => toggleSection('documentation')} />
              {expandedSections.documentation !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <PolicyFieldInput label="Sick Leave Certificate After (days)" value={form.documentation.sick_leave_certificate_days} onChange={v => updateField('documentation', 'sick_leave_certificate_days', v)} placeholder="e.g., 3" type="number" />
                  <PolicyFieldTextarea label="Other Required Documents" value={form.documentation.other_docs} onChange={v => updateField('documentation', 'other_docs', v)} placeholder="e.g., Medical certificate, travel proof..." />
                </div>
              )}
            </div>

            {/* Restrictions */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Restrictions" isOpen={expandedSections.restrictions !== false} onToggle={() => toggleSection('restrictions')} />
              {expandedSections.restrictions !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <PolicyFieldInput label="Blackout Dates" value={form.restrictions.blackout_dates} onChange={v => updateField('restrictions', 'blackout_dates', v)} placeholder="e.g., Dec 20-31, Quarter-end week" />
                  <PolicyFieldInput label="Max Consecutive Leave Days" value={form.restrictions.max_consecutive_days} onChange={v => updateField('restrictions', 'max_consecutive_days', v)} placeholder="e.g., 15" type="number" />
                  <PolicyFieldCheck label="Project-based restrictions apply" checked={form.restrictions.project_based} onChange={v => updateField('restrictions', 'project_based', v)} />
                  <PolicyFieldTextarea label="Additional Restrictions" value={form.restrictions.notes} onChange={v => updateField('restrictions', 'notes', v)} placeholder="Other restriction rules..." />
                </div>
              )}
            </div>

            {/* Compliance */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Compliance (Statutory)" isOpen={expandedSections.compliance !== false} onToggle={() => toggleSection('compliance')} />
              {expandedSections.compliance !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <PolicyFieldInput label="Statutory Leaves (per law)" value={form.compliance.statutory_leaves} onChange={v => updateField('compliance', 'statutory_leaves', v)} placeholder="e.g., As per Shops & Establishments Act" />
                  <PolicyFieldInput label="Maternity Leave (weeks)" value={form.compliance.maternity_weeks} onChange={v => updateField('compliance', 'maternity_weeks', v)} placeholder="e.g., 26" type="number" />
                  <PolicyFieldInput label="Paternity Leave (days)" value={form.compliance.paternity_days} onChange={v => updateField('compliance', 'paternity_days', v)} placeholder="e.g., 15" type="number" />
                  <PolicyFieldTextarea label="Notes" value={form.compliance.notes} onChange={v => updateField('compliance', 'notes', v)} placeholder="Compliance notes..." />
                </div>
              )}
            </div>

            {/* Tracking */}
            <div className="border border-slate-200 rounded-lg p-4">
              <PolicySectionHeader label="Tracking & Integration" isOpen={expandedSections.tracking !== false} onToggle={() => toggleSection('tracking')} />
              {expandedSections.tracking !== false && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <PolicyFieldCheck label="Integrated with Payroll" checked={form.tracking.integrated_with_payroll} onChange={v => updateField('tracking', 'integrated_with_payroll', v)} />
                  <PolicyFieldCheck label="Dashboard Visibility" checked={form.tracking.dashboard_visibility} onChange={v => updateField('tracking', 'dashboard_visibility', v)} />
                  <PolicyFieldTextarea label="Notes" value={form.tracking.notes} onChange={v => updateField('tracking', 'notes', v)} placeholder="Tracking notes..." />
                </div>
              )}
            </div>

            <Button data-testid="save-policy-btn" onClick={handleSave} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              <Save size={16} className="mr-2" />{editingId ? 'Update Policy' : 'Create Policy'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== ADMIN CONTROLS TAB ==================== */
function AdminControlsTab({ getHeaders, onRefresh }) {
  const [teamBalances, setTeamBalances] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editBalances, setEditBalances] = useState({});
  const [editReason, setEditReason] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditUserId, setAuditUserId] = useState(null);
  const [overrideDialog, setOverrideDialog] = useState(null); // {type: 'attendance'|'leave', ...}
  const [users, setUsers] = useState([]);
  const [carryForwardDialog, setCarryForwardDialog] = useState(null);
  const [cfBalances, setCfBalances] = useState({});
  const [cfReason, setCfReason] = useState('');

  const fetchTeamBalances = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hrms/admin/team-balances`, { headers: getHeaders() });
      setTeamBalances(res.data);
    } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
  }, [getHeaders]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: getHeaders() });
      setUsers(res.data);
    } catch (err) { console.error("./pages/LeaveManagement.jsx:", err); }
  }, [getHeaders]);

  useEffect(() => { fetchTeamBalances(); fetchUsers(); }, [fetchTeamBalances, fetchUsers]);

  const openEditBalance = (item) => {
    setEditingUser(item);
    setEditBalances({ ...item.balances });
    setEditReason('');
  };

  const saveBalance = async () => {
    try {
      await axios.put(`${API}/hrms/leave-balance/${editingUser.user_id}`, { balances: editBalances, reason: editReason || 'Admin manual update' }, { headers: getHeaders() });
      toast.success('Balance updated'); setEditingUser(null); fetchTeamBalances(); onRefresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  const recalculate = async (userId) => {
    try {
      const res = await axios.post(`${API}/hrms/leave-balance/${userId}/recalculate`, {}, { headers: getHeaders() });
      toast.success(res.data.message); fetchTeamBalances(); onRefresh();
    } catch (e) { toast.error('Failed to recalculate'); }
  };

  const viewAudit = async (userId) => {
    try {
      const res = await axios.get(`${API}/hrms/leave-audit-log/${userId}`, { headers: getHeaders() });
      setAuditLogs(res.data); setAuditUserId(userId);
    } catch (e) { toast.error('Failed to load audit log'); }
  };

  const submitAttendanceOverride = async () => {
    if (!overrideDialog) return;
    try {
      await axios.post(`${API}/hrms/attendance/admin-override`, {
        user_id: overrideDialog.user_id, date: overrideDialog.date,
        status: overrideDialog.status, reason: overrideDialog.reason
      }, { headers: getHeaders() });
      toast.success('Attendance overridden'); setOverrideDialog(null); onRefresh();
    } catch (e) { toast.error('Override failed'); }
  };

  const openCarryForward = (item) => {
    setCarryForwardDialog(item);
    const initCf = {};
    Object.keys(item.balances || {}).forEach(lt => { if (lt !== 'Leave Without Pay') initCf[lt] = 0; });
    setCfBalances(initCf);
    setCfReason('');
  };

  const submitCarryForward = async () => {
    if (!carryForwardDialog) return;
    const hasValues = Object.values(cfBalances).some(v => v > 0);
    if (!hasValues) { toast.error('Enter at least one carry forward value'); return; }
    if (!cfReason.trim()) { toast.error('Reason is required for audit trail'); return; }
    try {
      await axios.post(`${API}/hrms/leave-balance/${carryForwardDialog.user_id}/carry-forward`, {
        carry_forward: cfBalances, reason: cfReason
      }, { headers: getHeaders() });
      toast.success('Carry forward applied'); setCarryForwardDialog(null); fetchTeamBalances(); onRefresh();
    } catch (e) { toast.error('Failed to apply carry forward'); }
  };

  const leaveTypes = Object.keys(teamBalances[0]?.balances || {});

  return (
    <div data-testid="admin-controls-tab">
      {/* Admin override buttons */}
      <div className="flex gap-3 mb-4">
        <Button data-testid="override-attendance-btn" onClick={() => setOverrideDialog({ type: 'attendance', user_id: '', date: new Date().toISOString().slice(0, 10), status: 'present', reason: '' })} variant="outline" className="border-blue-300 text-blue-700 rounded text-xs">
          <Pencil size={14} className="mr-1" />Override Attendance
        </Button>
      </div>

      {/* Team Leave Balances */}
      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden mb-4">
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex justify-between items-center">
          <span className="font-bold text-blue-900 text-sm">Team Leave Balances</span>
          <span className="text-xs text-slate-500">{teamBalances.length} employees</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold text-blue-900 text-xs">Employee</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs">Role</TableHead>
                {leaveTypes.map(lt => (
                  <TableHead key={lt} className="font-bold text-blue-900 text-xs text-center">{lt.replace('Leave ', '').replace('Without ', 'W/O ')}</TableHead>
                ))}
                <TableHead className="font-bold text-blue-900 text-xs">Source</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamBalances.map(item => (
                <TableRow key={item.user_id} data-testid={`balance-row-${item.user_id}`} className="hover:bg-blue-50">
                  <TableCell className="font-semibold text-blue-900 text-sm">{item.user_name}</TableCell>
                  <TableCell><span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded uppercase font-bold">{item.role?.replace('_', ' ')}</span></TableCell>
                  {leaveTypes.map(lt => (
                    <TableCell key={lt} className={`text-center font-bold text-sm ${(item.balances[lt] || 0) === 0 ? 'text-red-500' : 'text-blue-900'}`}>
                      {lt === 'Leave Without Pay' ? 'LWP' : (item.balances[lt] ?? '-')}
                    </TableCell>
                  ))}
                  <TableCell><span className={`text-xs px-1.5 py-0.5 rounded font-bold ${item.source === 'admin_set' ? 'bg-purple-100 text-purple-800' : item.source === 'recalculated' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{item.source || 'default'}</span></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" data-testid={`edit-balance-${item.user_id}`} onClick={() => openEditBalance(item)} variant="outline" className="text-xs h-6 rounded border-blue-300"><Pencil size={10} className="mr-1" />Edit</Button>
                      <Button size="sm" data-testid={`carry-fwd-${item.user_id}`} onClick={() => openCarryForward(item)} variant="outline" className="text-xs h-6 rounded border-amber-300 text-amber-700">CF</Button>
                      <Button size="sm" data-testid={`recalc-${item.user_id}`} onClick={() => recalculate(item.user_id)} variant="outline" className="text-xs h-6 rounded border-emerald-300 text-emerald-700">Recalc</Button>
                      <Button size="sm" data-testid={`audit-${item.user_id}`} onClick={() => viewAudit(item.user_id)} variant="outline" className="text-xs h-6 rounded border-slate-300">Audit</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit Balance Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="rounded max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">Edit Leave Balance — {editingUser?.user_name}</DialogTitle>
            <DialogDescription>Manually set leave balances for this employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            {Object.entries(editBalances).filter(([k]) => k !== 'Leave Without Pay').map(([type, val]) => (
              <div key={type} className="flex items-center gap-3">
                <Label className="text-xs font-bold text-blue-900 w-32">{type}</Label>
                <Input type="number" value={val} onChange={e => setEditBalances(p => ({ ...p, [type]: parseFloat(e.target.value) || 0 }))} className="h-8 rounded border-slate-200 text-sm w-24" />
                <span className="text-xs text-slate-400">days</span>
              </div>
            ))}
            <div>
              <Label className="text-xs font-bold text-blue-900">Reason for change</Label>
              <Input data-testid="balance-edit-reason" value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="e.g., Annual reset, correction..." className="mt-1 h-8 rounded border-slate-200 text-sm" />
            </div>
            <Button data-testid="save-balance-btn" onClick={saveBalance} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Save size={14} className="mr-2" />Save Balance</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={!!auditUserId} onOpenChange={() => setAuditUserId(null)}>
        <DialogContent className="rounded max-w-2xl max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">Audit Log</DialogTitle>
            <DialogDescription>History of balance and attendance changes</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-3">
            {auditLogs.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No audit records</p>}
            {auditLogs.map(log => (
              <div key={log.id} className="bg-slate-50 rounded p-3 border border-slate-200 text-xs">
                <div className="flex justify-between">
                  <span className={`font-bold uppercase px-1.5 py-0.5 rounded ${log.type === 'balance_update' ? 'bg-purple-100 text-purple-800' : log.type === 'attendance_override' ? 'bg-blue-100 text-blue-800' : log.type === 'carry_forward' ? 'bg-amber-100 text-amber-800' : 'bg-amber-100 text-amber-800'}`}>{log.type?.replace('_', ' ')}</span>
                  <span className="text-slate-400">{log.created_at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                <p className="text-slate-700 mt-1"><span className="font-semibold">By:</span> {log.changed_by_name}</p>
                <p className="text-slate-700"><span className="font-semibold">Reason:</span> {log.reason || '-'}</p>
                {log.type === 'attendance_override' && <p className="text-slate-700"><span className="font-semibold">Date:</span> {log.date} | {log.old_status} → {log.new_status}</p>}
                {log.type === 'leave_override' && <p className="text-slate-700"><span className="font-semibold">Status:</span> {log.old_status} → {log.new_status}</p>}
                {log.type === 'balance_update' && log.old_values && (
                  <div className="mt-1 grid grid-cols-3 gap-1">
                    {Object.keys(log.new_values || {}).filter(k => k !== 'Leave Without Pay').map(k => (
                      <span key={k} className="text-slate-600">{k.replace('Leave ', '')}: {log.old_values[k] ?? '-'} → {log.new_values[k]}</span>
                    ))}
                  </div>
                )}
                {log.type === 'carry_forward' && log.carry_forward_values && (
                  <div className="mt-1 grid grid-cols-3 gap-1">
                    {Object.entries(log.carry_forward_values).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className="text-amber-700 font-semibold">{k.replace('Leave ', '')}: +{v}d</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Attendance Override Dialog */}
      <Dialog open={overrideDialog?.type === 'attendance'} onOpenChange={() => setOverrideDialog(null)}>
        <DialogContent className="rounded max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">Override Attendance</DialogTitle>
            <DialogDescription>Set attendance status for any employee on any date</DialogDescription>
          </DialogHeader>
          {overrideDialog?.type === 'attendance' && (
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs font-bold text-blue-900">Employee</Label>
                <select data-testid="override-user-select" value={overrideDialog.user_id} onChange={e => setOverrideDialog(p => ({ ...p, user_id: e.target.value }))} className="mt-1 flex h-9 w-full rounded border border-blue-200 bg-white px-3 text-sm sr-only">
                  <option value="">Select employee...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
                <div className="mt-1">
                  <SearchableSelect value={overrideDialog.user_id} onChange={(id) => setOverrideDialog(p => ({ ...p, user_id: id }))}
                    items={users} getSublabel={(u) => u.role}
                    emptyLabel="Select employee..." placeholder="Search employee…" testId="override-user-search" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold text-blue-900">Date</Label>
                <Input data-testid="override-date" type="date" value={overrideDialog.date} onChange={e => setOverrideDialog(p => ({ ...p, date: e.target.value }))} className="mt-1 h-9 rounded border-blue-200 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold text-blue-900">Status</Label>
                <select data-testid="override-status" value={overrideDialog.status} onChange={e => setOverrideDialog(p => ({ ...p, status: e.target.value }))} className="mt-1 flex h-9 w-full rounded border border-blue-200 bg-white px-3 text-sm">
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="half_day">Half Day</option>
                  <option value="on_leave">On Leave</option>
                  <option value="wfh">Work From Home</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold text-blue-900">Reason (mandatory for audit)</Label>
                <Input data-testid="override-reason" value={overrideDialog.reason} onChange={e => setOverrideDialog(p => ({ ...p, reason: e.target.value }))} placeholder="e.g., Late mark removal, client visit" className="mt-1 h-9 rounded border-blue-200 text-sm" />
              </div>
              <Button data-testid="submit-override-btn" onClick={submitAttendanceOverride} disabled={!overrideDialog.user_id || !overrideDialog.reason} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
                <Save size={14} className="mr-2" />Submit Override
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Carry Forward Dialog */}
      <Dialog open={!!carryForwardDialog} onOpenChange={() => setCarryForwardDialog(null)}>
        <DialogContent className="rounded max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">Carry Forward — {carryForwardDialog?.user_name}</DialogTitle>
            <DialogDescription>Add carried-forward leave days from previous system or year. These will be added on top of existing balance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-xs font-semibold text-amber-800">One-time carry forward adjustment. Enter days to ADD to current balance.</p>
            </div>
            {Object.entries(cfBalances).map(([type, val]) => (
              <div key={type} className="flex items-center gap-3">
                <Label className="text-xs font-bold text-blue-900 w-32">{type}</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-16 text-right">Current: {carryForwardDialog?.balances?.[type] ?? 0}</span>
                  <span className="text-xs text-slate-400">+</span>
                  <Input data-testid={`cf-${type.replace(/\s+/g, '-').toLowerCase()}`} type="number" min="0" step="0.5" value={val} onChange={e => setCfBalances(p => ({ ...p, [type]: parseFloat(e.target.value) || 0 }))} className="h-8 rounded border-slate-200 text-sm w-20" />
                  <span className="text-xs text-slate-400">days</span>
                </div>
              </div>
            ))}
            <div>
              <Label className="text-xs font-bold text-blue-900">Reason (required for audit)</Label>
              <Input data-testid="cf-reason" value={cfReason} onChange={e => setCfReason(e.target.value)} placeholder="e.g., Carry forward from previous financial year" className="mt-1 h-8 rounded border-slate-200 text-sm" />
            </div>
            <Button data-testid="submit-cf-btn" onClick={submitCarryForward} disabled={!cfReason.trim()} className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded font-bold">
              <Save size={14} className="mr-2" />Apply Carry Forward
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ==================== POLICY READ-ONLY VIEW ==================== */
function PolicyReadView({ policy }) {
  const Section = ({ title, children }) => (
    <div className="py-3 border-b border-slate-100 last:border-b-0">
      <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2">{title}</p>
      {children}
    </div>
  );
  const Field = ({ label, value }) => value ? (
    <div className="inline-block mr-6 mb-1"><span className="text-xs text-slate-500">{label}: </span><span className="text-xs font-semibold text-slate-800">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value).replace('_', ' ')}</span></div>
  ) : null;

  return (
    <div className="px-5 py-3 text-sm">
      {policy.leave_types?.length > 0 && (
        <Section title="Leave Types">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {policy.leave_types.map((lt, i) => (
              <div key={lt.name || i} className="bg-blue-50 rounded p-2 text-center">
                <p className="text-xs font-bold text-blue-900">{lt.name || 'Unnamed'}</p>
                <p className="text-lg font-extrabold text-blue-700">{lt.days_per_year || '-'}</p>
                <p className="text-xs text-slate-500">days/year</p>
                <div className="flex gap-1 justify-center mt-1 flex-wrap">
                  {lt.min_tenure_months > 0 && <span className="text-xs bg-indigo-100 text-indigo-800 px-1 rounded">{lt.min_tenure_months}m tenure</span>}
                  {lt.carry_forward && <span className="text-xs bg-emerald-100 text-emerald-800 px-1 rounded">CF</span>}
                  {lt.encashable && <span className="text-xs bg-amber-100 text-amber-800 px-1 rounded">Encash</span>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {policy.eligibility && (
        <Section title="Eligibility">
          <Field label="Categories" value={policy.eligibility.categories} />
          <Field label="Probation" value={policy.eligibility.probation_period} />
          <Field label="Min Tenure" value={policy.eligibility.min_tenure_months ? `${policy.eligibility.min_tenure_months} months` : null} />
        </Section>
      )}
      {policy.accrual_rules && (
        <Section title="Accrual Rules">
          <Field label="Method" value={policy.accrual_rules.method} />
          <Field label="Year Start" value={policy.accrual_rules.year_start === 'financial' ? 'Financial Year (Apr-Mar)' : 'Calendar Year (Jan-Dec)'} />
          <Field label="Credit Date" value={policy.accrual_rules.credit_date} />
          <Field label="Prorate" value={policy.accrual_rules.proration_for_new_joiners} />
        </Section>
      )}
      {policy.application_process && (
        <Section title="Application Process">
          <Field label="Min Notice" value={policy.application_process.min_notice_days ? `${policy.application_process.min_notice_days} days` : null} />
          <Field label="Max Consecutive" value={policy.application_process.max_consecutive_days ? `${policy.application_process.max_consecutive_days} days` : null} />
          <Field label="Half-day" value={policy.application_process.half_day_allowed} />
          {policy.application_process.notes && <p className="text-xs text-slate-600 mt-1">{policy.application_process.notes}</p>}
        </Section>
      )}
      {policy.approval_workflow && (
        <Section title="Approval Workflow">
          <Field label="1st Approver" value={policy.approval_workflow.first_approver || policy.approval_workflow.approvers} />
          <Field label="2nd Approver" value={policy.approval_workflow.second_approver} />
          <Field label="Escalation" value={policy.approval_workflow.escalation_days ? `${policy.approval_workflow.escalation_days} days` : null} />
          <Field label="Auto-approve WFH" value={policy.approval_workflow.auto_approve_wfh} />
          {policy.approval_workflow.notes && <p className="text-xs text-slate-600 mt-1">{policy.approval_workflow.notes}</p>}
        </Section>
      )}
      {policy.documentation && (
        <Section title="Documentation">
          <Field label="Sick Leave Certificate" value={policy.documentation.sick_leave_certificate_days ? `After ${policy.documentation.sick_leave_certificate_days} days` : null} />
          {policy.documentation.other_docs && <p className="text-xs text-slate-600 mt-1">{policy.documentation.other_docs}</p>}
        </Section>
      )}
      {policy.restrictions && (
        <Section title="Restrictions">
          <Field label="Blackout Dates" value={policy.restrictions.blackout_dates} />
          <Field label="Max Consecutive" value={policy.restrictions.max_consecutive_days ? `${policy.restrictions.max_consecutive_days} days` : null} />
          <Field label="Project-based" value={policy.restrictions.project_based} />
          {policy.restrictions.notes && <p className="text-xs text-slate-600 mt-1">{policy.restrictions.notes}</p>}
        </Section>
      )}
      {policy.compliance && (
        <Section title="Compliance">
          <Field label="Statutory" value={policy.compliance.statutory_leaves} />
          <Field label="Maternity" value={policy.compliance.maternity_weeks ? `${policy.compliance.maternity_weeks} weeks` : null} />
          <Field label="Paternity" value={policy.compliance.paternity_days ? `${policy.compliance.paternity_days} days` : null} />
          {policy.compliance.notes && <p className="text-xs text-slate-600 mt-1">{policy.compliance.notes}</p>}
        </Section>
      )}
      {policy.tracking && (
        <Section title="Tracking & Integration">
          <Field label="Payroll Integration" value={policy.tracking.integrated_with_payroll} />
          <Field label="Dashboard Visibility" value={policy.tracking.dashboard_visibility} />
          {policy.tracking.notes && <p className="text-xs text-slate-600 mt-1">{policy.tracking.notes}</p>}
        </Section>
      )}
    </div>
  );
}
